/**
 * The pre-flight sheet — the concept's `flow/Preflight.jsx`, reading
 * `POST /flows/:id/preflight` instead of computing its own checks.
 *
 * The concept's `preflight()` ran in the browser over sample data. Here the
 * API runs it over the saved draft and the real workspace: the audience it
 * would enrol, the templates' approval, consent per channel, the projected
 * cost from credit prices. The sheet only draws it, so the checks a person
 * reads and the checks `POST /launch` enforces are one list — Launch is
 * disabled exactly when `blocking` is true, and the API refuses a launch with
 * the same checks if they changed in between.
 *
 * Unknowns stay unknown: an event-triggered flow with no audience has no
 * "people at launch", and the contract says null — drawn as a sentence, never
 * as 0 people and ₹0. A cost with no price set for a channel is "no price set",
 * not free.
 *
 * A check whose fix names a step that is still a suggestion offers Accept and
 * Remove right here (the concept's behaviour); any other fix closes the sheet
 * and opens that step or that port's "+". Focus is trapped while it is open
 * and returns to Launch when it closes.
 */
import type { FlowPreflightCheck, FlowPreflightResponse } from '@wend/shared';
import { cn } from '@wend/ui';
import { useEffect, useRef, type KeyboardEvent } from 'react';

import { HINT, KICK } from './controls';
import { BTN_GHOST, ICON_BTN } from './forms';
import { IcCheck, IcClose, IcSend, IcWarn } from './icons';
import { n, rupees } from './model';

const TONE_ICON = { good: IcCheck, warn: IcWarn, bad: IcWarn } as const;

export interface PreflightProps {
  pf: FlowPreflightResponse | null;
  loading: boolean;
  error: string | null;
  version: number;
  relaunch: boolean;
  paused: boolean;
  launching: boolean;
  /** Ids of steps that are still suggestions, so their checks can offer Accept / Remove. */
  suggestedIds: ReadonlySet<string>;
  onClose: () => void;
  onLaunch: () => void;
  onRetry: () => void;
  onFix: (check: FlowPreflightCheck) => void;
  onAccept: (nodeId: string) => void;
  onReject: (nodeId: string) => void;
}

export function Preflight({ pf, loading, error, version, relaunch, paused, launching, suggestedIds, onClose, onLaunch, onRetry, onFix, onAccept, onReject }: PreflightProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[data-sheet-close]')?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
    if (event.key === 'Tab' && ref.current) {
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('button:not([disabled])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const checks = pf?.checks ?? [];
  const bad = checks.filter((c) => c.tone === 'bad').length;
  const warn = checks.filter((c) => c.tone === 'warn').length;
  const blocking = pf === null || pf.blocking;
  const cost = pf?.projectedCost ?? null;

  return (
    <div className="absolute inset-0 z-[45] grid place-items-center bg-[rgba(5,4,14,.68)] p-4 backdrop-blur-[3px]" onClick={onClose}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flow-pf-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={onKeyDown}
        className="max-h-full w-[min(660px,100%)] overflow-auto rounded-2xl border border-line-strong bg-surface-2 px-5 py-[18px] shadow-lift motion-safe:animate-pop"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className={KICK}>Pre-flight · Version {version}</p>
            <h3 id="flow-pf-title" className="mb-3.5 mt-1 text-[1.3rem] font-semibold">
              {relaunch ? `Publish Version ${version}?` : 'Ready to launch?'}
            </h3>
          </div>
          <button type="button" data-sheet-close="" className={ICON_BTN} aria-label="Close pre-flight" onClick={onClose}>
            <IcClose size={16} />
          </button>
        </div>

        {loading && !pf && <p className={HINT} aria-live="polite">Checking the saved draft against your workspace…</p>}
        {error && (
          <div className="flex flex-wrap items-center gap-2 rounded-[10px] border border-bad/45 bg-bad/6 px-3 py-2 text-[13px] text-bad" role="alert">
            {error}
            <button type="button" className={BTN_GHOST} onClick={onRetry}>Try again</button>
          </div>
        )}

        {pf && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <section className="rounded-xl border border-line bg-white/[0.03] px-3.5 py-3">
                <p className={KICK}>Audience at launch</p>
                {pf.audience.people === null ? (
                  <p className={cn(HINT, 'mt-1.5')}>Not known until people arrive — this flow enrols as its trigger fires, not from a list at launch.</p>
                ) : (
                  <>
                    <div className="mb-1.5 mt-0.5 text-[1.75rem] font-semibold tabular-nums tracking-[-0.03em] text-white" data-role="pf-people">
                      {n(pf.audience.people)} <small className="text-[13px] font-normal tracking-normal text-muted">people</small>
                    </div>
                    <ul className="m-0 list-none p-0 text-[13px]">
                      <li className="flex justify-between gap-2.5 border-t border-dashed border-line py-1 text-muted">
                        <span>Reachable on WhatsApp</span>
                        <b className="font-semibold tabular-nums text-white">{pf.audience.phoneReachable === null ? '—' : n(pf.audience.phoneReachable)}</b>
                      </li>
                      <li className="flex justify-between gap-2.5 border-t border-dashed border-line py-1 text-muted">
                        <span>Reachable by email</span>
                        <b className="font-semibold tabular-nums text-white">{pf.audience.emailReachable === null ? '—' : n(pf.audience.emailReachable)}</b>
                      </li>
                    </ul>
                  </>
                )}
              </section>
              <section className="rounded-xl border border-line bg-white/[0.03] px-3.5 py-3">
                <p className={KICK}>Estimated cost · upper bound</p>
                {cost === null ? (
                  <p className={cn(HINT, 'mt-1.5')}>No estimate: the audience is not known yet.</p>
                ) : (
                  <>
                    <div className="mb-1.5 mt-0.5 text-[1.75rem] font-semibold tabular-nums tracking-[-0.03em] text-white" data-role="pf-cost">
                      {cost.totalMinor === null ? 'No price set' : rupees(cost.totalMinor)}
                    </div>
                    <ul className="m-0 list-none p-0 text-[13px]">
                      <li className="flex justify-between gap-2.5 border-t border-dashed border-line py-1 text-muted">
                        <span>WhatsApp · {n(cost.whatsappMessages)} messages</span>
                        <b className="font-semibold tabular-nums text-white">{cost.whatsappMinor === null ? 'No price set' : rupees(cost.whatsappMinor)}</b>
                      </li>
                      <li className="flex justify-between gap-2.5 border-t border-dashed border-line py-1 text-muted">
                        <span>Email · {n(cost.emailMessages)} messages</span>
                        <b className="font-semibold tabular-nums text-white">{cost.emailMinor === null ? 'No price set' : rupees(cost.emailMinor)}</b>
                      </li>
                    </ul>
                    <p className={HINT}>Assumes everyone takes every send on their path, at your credit prices.</p>
                  </>
                )}
              </section>
            </div>

            <p className={cn(KICK, 'mt-[18px]')} aria-live="polite">
              Checks · {checks.length - bad - warn} passed{warn ? ` · ${warn} to review` : ''}{bad ? ` · ${bad} blocking` : ''}
            </p>
            <ul className="mt-2 flex list-none flex-col gap-1.5 p-0" data-role="pf-checks">
              {checks.map((check) => {
                const Icon = TONE_ICON[check.tone];
                const sugId = check.fix?.nodeId && suggestedIds.has(check.fix.nodeId) ? check.fix.nodeId : null;
                return (
                  <li
                    key={check.id}
                    data-tone={check.tone}
                    className={cn(
                      'flex gap-2.5 rounded-[10px] border px-3 py-2.5 text-[13px]',
                      check.tone === 'good' && 'border-line',
                      check.tone === 'warn' && 'border-warn/35 bg-warn/5',
                      check.tone === 'bad' && 'border-bad/45 bg-bad/6',
                    )}
                  >
                    <span
                      className={cn(
                        'grid size-6 flex-none place-items-center rounded-full',
                        check.tone === 'good' && 'bg-good/14 text-good',
                        check.tone === 'warn' && 'bg-warn/14 text-warn',
                        check.tone === 'bad' && 'bg-bad/15 text-bad',
                      )}
                    >
                      <Icon size={14} />
                    </span>
                    <div className="min-w-0">
                      <b className="block font-semibold text-white">{check.title}</b>
                      <span className="text-muted">{check.body}</span>
                      {sugId ? (
                        <span className="mt-2 flex flex-wrap gap-2">
                          <button type="button" className={BTN_GHOST} onClick={() => onAccept(sugId)}>
                            <IcCheck size={14} /> Accept suggestion
                          </button>
                          <button type="button" className={BTN_GHOST} onClick={() => onReject(sugId)}>
                            Remove it
                          </button>
                        </span>
                      ) : (
                        check.fix && (
                          <span className="mt-2 flex flex-wrap gap-2">
                            <button type="button" className={BTN_GHOST} onClick={() => onFix(check)}>
                              {check.fix.label}
                            </button>
                          </span>
                        )
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-3.5">
          <span className={cn(HINT, 'flex-[1_1_200px]')}>
            {pf === null
              ? 'Launch opens once the checks have run.'
              : blocking
                ? 'Resolve the blocking items to launch.'
                : relaunch
                  ? `People already inside finish on the version they entered.${paused ? ' The flow is paused and stays paused after publishing — resume it from Run.' : ''}`
                  : 'You can pause at any time. Quiet hours hold anything due inside them.'}
          </span>
          <button type="button" className={BTN_GHOST} onClick={onClose}>
            Back to editing
          </button>
          <button
            type="button"
            data-role="pf-launch"
            disabled={blocking || launching}
            onClick={onLaunch}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-[9px] bg-white px-3.5 py-2 text-[13px] font-semibold text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b disabled:cursor-not-allowed disabled:opacity-40"
          >
            <IcSend size={15} /> {launching ? 'Launching…' : relaunch ? `Publish Version ${version}` : 'Launch flow'}
          </button>
        </div>
      </div>
    </div>
  );
}
